import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import screens from './Screens.module.css';
import styles from './Dashboard.module.css';
import { Button, MatchRow, Spinner } from '../../components/ui';
import { AccuracyWidget } from '../../components/app/AccuracyWidget.jsx';
import { getTopPicks } from '../../api/platform.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { formatDate, formatMatchTime, useI18n } from '../../i18n/index.jsx';

export function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [picks, setPicks] = useState({ status: 'loading' });

  useEffect(() => {
    document.title = `${t('nav_home')} — Don Quack`;
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    getTopPicks()
      .then((res) => {
        if (cancelled) return;
        setPicks({
          status: res.locked ? 'locked' : 'ready',
          items: res.picks.slice(0, 2),
        });
      })
      .catch(() => !cancelled && setPicks({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, []);

  const hour = new Date().getHours();
  const greetingKey =
    hour < 12 ? 'greeting_morning' : hour < 18 ? 'greeting_afternoon' : 'greeting_evening';

  // The account has no display name — the local part of the email is the
  // friendliest thing we actually know about them.
  const name = user?.email ? user.email.split('@')[0] : '';

  return (
    <section>
      <h2 className={screens.title}>
        {t(greetingKey)}
        {name ? `, ${name}` : ''} 👋
      </h2>
      <p className={screens.sub}>
        {t('dash_subtitle', {
          date: formatDate(new Date(), lang, { weekday: 'long', day: 'numeric', month: 'long' }),
        })}
      </p>

      <AccuracyWidget variant="hero" className={screens.blockGap} />

      <div className={screens.ctaCard}>
        <img src="/duck.webp" alt="" width="74" height="74" />
        <div>
          <h3>{t('dash_cta_title')}</h3>
          <p>{t('dash_cta_body')}</p>
        </div>
        <Button to="/app/forecast">{t('dash_cta_button')} 🦆</Button>
      </div>

      <div className={screens.sectionHead}>
        <h3>{t('dash_todays_picks')}</h3>
        {picks.status === 'ready' && (
          <Link className="link" to="/app/top-picks">
            {t('dash_see_all')} →
          </Link>
        )}
      </div>

      {picks.status === 'loading' && (
        <div className={screens.loadingBlock}>
          <Spinner />
        </div>
      )}

      {picks.status === 'error' && <div className={screens.errorBox}>{t('error_generic')}</div>}

      {/* Non-Premium accounts receive no pick data at all, so this is a prompt
          rather than blurred cards. */}
      {picks.status === 'locked' && (
        <div className={styles.upsell}>
          <span className={styles.lock} aria-hidden="true">
            🔒
          </span>
          <div>
            <h4>{t('top_picks_locked_title')}</h4>
            <p>{t('top_picks_locked_body')}</p>
          </div>
          <Button to="/app/pricing">{t('gate_cta')}</Button>
        </div>
      )}

      {picks.status === 'ready' &&
        (picks.items.length === 0 ? (
          <p className="muted">{t('top_picks_empty')}</p>
        ) : (
          <div className={screens.twoCol}>
            {picks.items.map((p) => (
              <MatchRow
                key={p.id}
                league={p.league}
                confidence={p.confidence}
                confidenceLabel={t(`confidence_${p.confidence}_short`)}
                homeTeam={p.home}
                awayTeam={p.away}
                homeProbability={p.homeProbability}
                winner={p.winner}
                predictedWinner={p.predictedWinner}
                kickoff={p.matchStartUtc ? formatMatchTime(p.matchStartUtc, lang) : null}
              />
            ))}
          </div>
        ))}
    </section>
  );
}
